// Admin panel for the synthetic-V6 test rig. Generates a recording
// with the given parameters, runs the full analysis pipeline, and
// renders a verification report (detected vs ground truth across 10
// pipeline metrics). The new recording lands as a regular row in the
// system, openable via "Open recording →" or via Browse.
//
// Useful for exercising the analysis end-to-end against known truth —
// changes to RPM detectors, ICA alignment, per-cyl slot binning all
// show up here as pass/fail.

import { useState } from 'react';
import { api, SimulationResult } from './api';

interface Props {
  onOpenRecording: (recordingId: number) => void;
}

export function SimulationPanel({ onOpenRecording }: Props) {
  const [rpm, setRpm] = useState<number>(1800);
  const [duration, setDuration] = useState<number>(5);
  const [seed, setSeed] = useState<number>(42);
  const [resetTemplate, setResetTemplate] = useState<boolean>(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.post<SimulationResult>('/api/admin/simulation/generate', {
        rpm, duration_sec: duration, seed, reset_template: resetTemplate,
      });
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Simulation</h3>
      <div className="muted" style={{ fontSize: '0.85rem', marginBottom: 8 }}>
        Generate a synthetic 6-cyl 4-stroke V6 recording with known
        ground truth (per-cyl odd-harmonic squarewave bursts at
        distinct fundamentals + ambient noise). The resulting clip is
        saved like any other recording, runs through the full analysis
        pipeline (ICA + alignment), and gets a pass/fail verification
        report. Repeated runs add new clips to the same vehicle (user
        <code> demo1 </code> → <code> Synthetic V6 (test rig) </code>).
      </div>
      <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="field" style={{ width: 130 }}>
          <label>RPM (250-4500)</label>
          <input
            type="number" min={250} max={4500} step={50}
            value={rpm}
            onChange={(e) => setRpm(Math.max(250, Math.min(4500, Number(e.target.value) || 1800)))}
            disabled={busy}
          />
        </div>
        <div className="field" style={{ width: 130 }}>
          <label>Duration (s)</label>
          <input
            type="number" min={2} max={60} step={1}
            value={duration}
            onChange={(e) => setDuration(Math.max(2, Math.min(60, Number(e.target.value) || 5)))}
            disabled={busy}
          />
        </div>
        <div className="field" style={{ width: 130 }}>
          <label>RNG seed</label>
          <input
            type="number" min={0} step={1}
            value={seed}
            onChange={(e) => setSeed(Math.max(0, Number(e.target.value) || 0))}
            disabled={busy}
          />
        </div>
        <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            margin: 0, fontSize: '0.78rem', color: 'var(--muted)',
          }} title="Wipe per-vehicle template + cyl mapping before ingesting so each synth bootstraps independently. Recommended for sanity-check use.">
            <input
              type="checkbox" checked={resetTemplate}
              onChange={(e) => setResetTemplate(e.target.checked)}
              disabled={busy}
              style={{ width: 'auto' }}
            />
            reset template
          </label>
        </div>
        <div className="field">
          <button onClick={run} disabled={busy}>
            {busy ? 'Running…' : 'Generate synthetic V6'}
          </button>
        </div>
        {result && (
          <div className="field">
            <button
              className="secondary"
              onClick={() => onOpenRecording(result.recording_id)}
            >
              Open recording #{result.recording_id} →
            </button>
          </div>
        )}
      </div>

      {error && <div className="error" style={{ marginTop: 6 }}>{error}</div>}

      {result && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: '0.85rem', marginBottom: 6 }}>
            <strong style={{ color: result.ok ? 'var(--ok)' : 'var(--danger)' }}>
              {result.ok ? '✓ All checks passed' : '✗ Some checks failed'}
            </strong>
            {' — '}
            recording <strong>#{result.recording_id}</strong>,
            vehicle <strong>#{result.vehicle_id}</strong>,
            file <code>data/recordings/{result.user_id}/{result.vehicle_id}/{result.recording_id}.flac</code>
          </div>
          <table style={{ fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ color: 'var(--muted)' }}>
                <th></th><th>Check</th><th>Expected</th><th>Got</th>
              </tr>
            </thead>
            <tbody>
              {result.checks.map((c, i) => (
                <tr key={i}>
                  <td style={{
                    color: c.ok ? 'var(--ok)' : 'var(--danger)',
                    fontWeight: 700, width: 18,
                  }}>{c.ok ? '✓' : '✗'}</td>
                  <td>{c.label}</td>
                  <td className="muted">{c.expected}</td>
                  <td>{c.got}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
            Open the recording and load the per-cylinder ensemble + spectrum
            views to visually confirm the 6 cyls have distinct signatures.
            Per-cyl fundamentals (in firing-order):
            cyl 1 = 600 Hz × 3 harm,
            cyl 2 = 700 × 4,
            cyl 3 = 800 × 5,
            cyl 4 = 1000 × 6,
            cyl 5 = 1200 × 7,
            cyl 6 = 1500 × 3.
          </div>
        </div>
      )}
    </div>
  );
}
